import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ArrowLeft, Save, Loader2, CalendarIcon, AlertTriangle, Ship, Plane } from "lucide-react";
import { toast } from "sonner";
import { format, addYears, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "../lib/utils";

const initialTallas = Array.from({ length: 20 }, (_, i) => ({
  talla: "",
  num_bultos: null,
  peso_por_bulto: null,
  kilos_empaque: null, // Kilos totales para esta talla, divide entre bultos para calcular peso_por_bulto
  empaque_seleccionado: "", // Empaque específico seleccionado para esta talla
}));

export default function BatchForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const isEditing = !!id;
  const isReadOnly = user?.role === "cliente";
  const isProveedor = user?.role === "proveedor";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [logistics, setLogistics] = useState([]);
  const [transportCompanies, setTransportCompanies] = useState([]);
  const [lugaresDestino, setLugaresDestino] = useState([]);
  const [puestosControl, setPuestosControl] = useState([]);
  const [showWarning, setShowWarning] = useState(null);
  const [numeroLoteManual, setNumeroLoteManual] = useState(false); // True si el usuario editó manualmente

  const [formData, setFormData] = useState({
    numero_control: null,
    numero_lote: "",
    producto_id: "",
    producto_nombre: "",
    tipo_envio: "",
    fecha_tentativa_embarque: "",
    fecha_reconocimiento: "",
    salida_buque: "",
    llegada_estimada: "",
    llegada_estimada_original: "",
    llegada_estimada_historial: [],
    desviacion_dias: null,
    desviacion_porcentaje: null,
    fecha_captura: "",
    fecha_caducidad: "",
    fecha_congelacion: "",
    factura_origen: "",
    factura_destino: "",
    fecha_factura_origen: "",  // Fecha personalizada para factura origen
    fecha_factura_cliente: "",  // Fecha personalizada para factura cliente
    peso_neto: 0,
    bultos: 0,
    peso_cajas: 1,
    palets: 0,
    kg_palet: 0,
    tara: 0,
    peso_contenedor: 0,
    peso_bruto: 0,
    kilos_facturables: 0,
    destare_cliente: "",  // Cambio a string vacío para mejor UX
    tallas: initialTallas,
    cliente_id: "",
    cliente_nombre: "",
    proveedor_id: "",
    proveedor_nombre: "",
    logistica_id: "",
    logistica_nombre: "",
    transporte_id: "",
    transporte_nombre: "",
    awb_aereo: "",
    bl_maritimo: "",
    contenedor: "",
    precintos: "",
    puerto_desembarco: "",
    identificacion_buque: "",  // Nuevo campo para informe CSO
    lugar_destino_id: "",      // Nuevo campo
    lugar_destino_nombre: "",  // Nuevo campo
    puesto_control_id: "",     // Nuevo campo
    puesto_control_nombre: "", // Nuevo campo
    presentacion: "",
    empaque: "",
    nombre_cientifico: "",
    zona_fao: "",
    arte_pesca: "",
    codigo_alfa: "",
    pais_origen: "",
    codigo_arancelario: "",
    alergenos: "",
    precio_coste: 0,  // USD
    precio_venta: 0,  // EUR
    cambio_bcv: 0,  // Tasa BCV para BS
    cotizacion_eur_usd: 0,  // Cotización EUR/USD
    descuento_porcentaje: "",  // Descuento % sobre precio unitario
    descuento_euros: "",  // Descuento € sobre precio unitario
    certificado_exportacion: "",
    dua: "",
    observaciones: "",
    comentarios_privados: "",  // Solo visible para admin y proveedor asignado
    estado: "documentacion_inicial",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showCotizacionDialog, setShowCotizacionDialog] = useState(false);
  const [tempCotizacion, setTempCotizacion] = useState("");

  useEffect(() => {
    fetchRelatedData();
    if (isEditing) {
      fetchBatch();
    } else {
      fetchNextControlNumber();
    }
  }, [id]);

  // Auto-asignar proveedor cuando es rol proveedor con UNA sola empresa
  useEffect(() => {
    if (!isEditing && isProveedor && suppliers.length > 0) {
      const userProveedorIds = user?.proveedor_ids || (user?.proveedor_id ? [user.proveedor_id] : []);
      // Solo auto-asignar si tiene exactamente 1 proveedor
      if (userProveedorIds.length === 1) {
        const supplierData = suppliers.find(s => s.id === userProveedorIds[0]);
        setFormData((prev) => ({
          ...prev,
          proveedor_id: userProveedorIds[0],
          proveedor_nombre: supplierData?.nombre || "",
        }));
      }
    }
  }, [isEditing, isProveedor, user?.proveedor_id, user?.proveedor_ids, suppliers]);

  // Proveedores disponibles para el usuario proveedor (filtrar solo los asignados)
  const availableSuppliers = isProveedor 
    ? suppliers.filter(s => {
        const userProveedorIds = user?.proveedor_ids || (user?.proveedor_id ? [user.proveedor_id] : []);
        return userProveedorIds.includes(s.id);
      })
    : suppliers;

  // ¿El proveedor tiene múltiples empresas?
  const proveedorTieneMultiples = isProveedor && (user?.proveedor_ids?.length > 1);

  const fetchRelatedData = async () => {
    try {
      const [prodRes, clientRes, suppRes, logRes, transRes, lugaresRes, puestosRes] = await Promise.all([
        api.get("/products"),
        api.get("/clients"),
        api.get("/suppliers"),
        api.get("/logistics"),
        api.get("/transport-companies"),
        api.get("/lugares-destino"),
        api.get("/puestos-control"),
      ]);
      setProducts(prodRes.data);
      setClients(clientRes.data);
      setSuppliers(suppRes.data);
      setLogistics(logRes.data);
      setTransportCompanies(transRes.data);
      setLugaresDestino(lugaresRes.data);
      setPuestosControl(puestosRes.data);
    } catch (error) {
      console.error("Error fetching related data:", error);
    }
  };

  const fetchBatch = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/batches/${id}`);
      const batchData = response.data;
      // Formatear numero_control con 6 dígitos
      if (batchData.numero_control) {
        batchData.numero_control = String(batchData.numero_control).padStart(6, "0");
      }
      // Normalizar campos de descuento: null -> "" para que los inputs funcionen
      setFormData({
        ...batchData,
        tallas: batchData.tallas || initialTallas,
        descuento_porcentaje: batchData.descuento_porcentaje ?? "",
        descuento_euros: batchData.descuento_euros ?? "",
        destare_cliente: batchData.destare_cliente ?? "",
      });
    } catch (error) {
      toast.error("Error al cargar el lote");
      navigate("/batches");
    } finally {
      setLoading(false);
    }
  };

  const fetchNextControlNumber = async () => {
    try {
      const response = await api.get("/batches/next-control-number");
      // Formatear con 6 dígitos (000001, 000002, etc.)
      const numControl = String(response.data.next_control_number).padStart(6, "0");
      setFormData((prev) => ({ ...prev, numero_control: numControl }));
    } catch (error) {
      console.error("Error fetching next control number:", error);
    }
  };

  // Generar número de lote automáticamente
  const generateLoteNumber = useCallback(async (productoNombre, tipoEnvio) => {
    if (!productoNombre || !tipoEnvio) return "";

    const words = productoNombre.trim().split(/\s+/);
    const firstWord = words[0] || "";
    const secondWord = words[1] || "";

    // Dos primeras letras de la primera palabra
    const prefix1 = firstWord.substring(0, 2).toUpperCase();
    // Primera letra de la segunda palabra
    const prefix2 = secondWord.charAt(0).toUpperCase();

    const today = new Date();
    const year = today.getFullYear().toString().slice(-2);
    const month = (today.getMonth() + 1).toString().padStart(2, "0");
    const day = today.getDate().toString().padStart(2, "0");

    const suffix = tipoEnvio === "maritimo" ? "B" : "A";
    const dateStr = `${year}${month}${day}`;

    // Contar lotes existentes de ese día y tipo
    try {
      const response = await api.get(`/batches/count-by-date?date=${dateStr}&tipo_envio=${tipoEnvio}`);
      const count = response.data.count + 1;
      const sequence = count.toString().padStart(3, "0");

      return `${prefix1}${prefix2}${dateStr}${suffix}${sequence}`;
    } catch (error) {
      return `${prefix1}${prefix2}${dateStr}${suffix}001`;
    }
  }, []);

  // Manejar cambio de producto
  const handleProductChange = async (productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      // Solo generar número de lote automáticamente si no se ha editado manualmente
      let nuevoLote = formData.numero_lote;
      if (!numeroLoteManual && formData.tipo_envio) {
        nuevoLote = await generateLoteNumber(product.nombre, formData.tipo_envio);
      }

      setFormData((prev) => ({
        ...prev,
        producto_id: productId,
        producto_nombre: product.nombre,
        numero_lote: nuevoLote,
        presentacion: product.presentacion || "",
        empaque: product.empaque || "",
        nombre_cientifico: product.nombre_cientifico || "",
        zona_fao: product.zona_fao || "",
        arte_pesca: product.arte_pesca || "",
        codigo_alfa: product.codigo_alfa || "",
        pais_origen: product.pais_origen || "",
        codigo_arancelario: product.codigo_arancelario || "",
        alergenos: product.alergenos || "",
        // Copiar precios del producto si están definidos
        precio_coste: product.precio_coste || prev.precio_coste || "",
        precio_venta: product.precio_venta || prev.precio_venta || "",
      }));
    }
  };

  // Manejar cambio de tipo de envío
  const handleTipoEnvioChange = async (tipo) => {
    // Solo generar número de lote automáticamente si no se ha editado manualmente
    let nuevoLote = formData.numero_lote;
    if (!numeroLoteManual && formData.producto_nombre) {
      nuevoLote = await generateLoteNumber(formData.producto_nombre, tipo);
    }

    // Asignar operador logístico por defecto según tipo de envío
    let logisticaDefecto = formData.logistica_id;
    let logisticaNombreDefecto = formData.logistica_nombre;
    
    // Buscar ABAD para marítimo y DECOEXSA para aéreo
    if (tipo === "maritimo") {
      const abad = logistics.find(l => l.nombre?.toUpperCase().includes("ABAD"));
      if (abad) {
        logisticaDefecto = abad.id;
        logisticaNombreDefecto = abad.nombre;
      }
    } else if (tipo === "aereo") {
      const decoexsa = logistics.find(l => l.nombre?.toUpperCase().includes("DECOEXSA"));
      if (decoexsa) {
        logisticaDefecto = decoexsa.id;
        logisticaNombreDefecto = decoexsa.nombre;
      }
    }

    setFormData((prev) => ({
      ...prev,
      tipo_envio: tipo,
      numero_lote: nuevoLote,
      logistica_id: logisticaDefecto,
      logistica_nombre: logisticaNombreDefecto,
    }));
  };

  // Manejar cambio manual del número de lote
  const handleNumeroLoteChange = (value) => {
    setNumeroLoteManual(true); // Marcar como editado manualmente
    setFormData((prev) => ({ ...prev, numero_lote: value }));
  };

  // Formatear Date a YYYY-MM-DD manteniendo la fecha local (helper para cálculos)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Calcular fecha de caducidad desde fecha de congelación (+2 años - 1 día)
  const calcularCaducidad = (congelacionStr) => {
    if (!congelacionStr) return "";
    const [year, month, day] = congelacionStr.split("-").map(Number);
    const congelacionDate = new Date(year, month - 1, day);
    let caducidadDate = addYears(congelacionDate, 2);
    caducidadDate.setDate(caducidadDate.getDate() - 1);
    return formatDateLocal(caducidadDate);
  };

  // Manejar cambio de fecha de captura - auto-rellena congelación y caducidad
  const handleFechaCapturaChange = (dateStr) => {
    if (dateStr) {
      const caducidadStr = calcularCaducidad(dateStr);
      setFormData((prev) => ({
        ...prev,
        fecha_captura: dateStr,
        fecha_congelacion: dateStr, // Auto-rellena congelación con la misma fecha
        fecha_caducidad: caducidadStr, // Auto-calcula caducidad
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        fecha_captura: "",
      }));
    }
  };

  // Manejar cambio de fecha de congelación - calcula caducidad (+2 años - 1 día)
  const handleFechaCongelacionChange = (dateStr) => {
    if (dateStr) {
      const caducidadStr = calcularCaducidad(dateStr);
      setFormData((prev) => ({
        ...prev,
        fecha_congelacion: dateStr,
        fecha_caducidad: caducidadStr,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        fecha_congelacion: "",
        fecha_caducidad: "",
      }));
    }
  };

  // Validar fecha de reconocimiento vs salida buque
  const validateFechaReconocimiento = () => {
    if (formData.fecha_reconocimiento && formData.salida_buque) {
      const reconDate = new Date(formData.fecha_reconocimiento);
      const salidaDate = new Date(formData.salida_buque);
      if (reconDate > salidaDate) {
        setValidationErrors((prev) => ({
          ...prev,
          fecha_reconocimiento: "La fecha de reconocimiento no puede ser posterior a la salida del buque",
        }));
        return false;
      }
    }
    setValidationErrors((prev) => {
      const { fecha_reconocimiento, ...rest } = prev;
      return rest;
    });
    return true;
  };

  // Manejar cambio de llegada estimada
  const handleLlegadaEstimadaChange = (dateStr) => {
    const newHistorial = [...(formData.llegada_estimada_historial || [])];
    
    if (formData.llegada_estimada && dateStr !== formData.llegada_estimada) {
      newHistorial.push(formData.llegada_estimada);
    }

    let desviacionDias = null;
    let desviacionPorcentaje = null;

    const original = formData.llegada_estimada_original || dateStr;
    if (original && dateStr) {
      const originalDate = new Date(original);
      const newDate = new Date(dateStr);
      desviacionDias = differenceInDays(newDate, originalDate);
      
      // Calcular porcentaje basado en tiempo total de tránsito esperado
      if (formData.salida_buque) {
        const salidaDate = new Date(formData.salida_buque);
        const transitoEsperado = differenceInDays(originalDate, salidaDate);
        if (transitoEsperado > 0) {
          desviacionPorcentaje = (desviacionDias / transitoEsperado) * 100;
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      llegada_estimada: dateStr,
      llegada_estimada_original: prev.llegada_estimada_original || dateStr,
      llegada_estimada_historial: newHistorial,
      desviacion_dias: desviacionDias,
      desviacion_porcentaje: desviacionPorcentaje ? Math.round(desviacionPorcentaje * 100) / 100 : null,
    }));
  };

  // Calcular TARA y Peso Bruto
  const calculateWeights = useCallback(() => {
    const taraCajas = (formData.bultos || 0) * (formData.peso_cajas || 0);
    const taraPalets = (formData.palets || 0) * (formData.kg_palet || 0);
    const tara = taraCajas + taraPalets;
    const pesoBruto = (formData.peso_neto || 0) + tara + (formData.peso_contenedor || 0);

    setFormData((prev) => ({
      ...prev,
      tara: Math.round(tara * 100) / 100,
      peso_bruto: Math.round(pesoBruto * 100) / 100,
    }));
  }, [formData.bultos, formData.peso_cajas, formData.palets, formData.kg_palet, formData.peso_neto, formData.peso_contenedor]);

  useEffect(() => {
    calculateWeights();
  }, [formData.bultos, formData.peso_cajas, formData.palets, formData.kg_palet, formData.peso_neto, formData.peso_contenedor]);

  // Validar tallas y calcular kilos facturables
  const validateTallas = useCallback(() => {
    // Calcular totales de las tallas
    const totalBultosTallas = (formData.tallas || []).reduce((sum, t) => {
      return sum + (t.num_bultos || 0);
    }, 0);

    const totalKilosFacturables = (formData.tallas || []).reduce((sum, t) => {
      if (t.num_bultos && t.peso_por_bulto) {
        return sum + t.num_bultos * t.peso_por_bulto;
      }
      return sum;
    }, 0);

    // Solo actualizar kilos facturables si el campo está vacío o es 0
    // Esto permite edición manual sin sobrescribir
    if (!formData.kilos_facturables || formData.kilos_facturables === 0) {
      setFormData((prev) => ({
        ...prev,
        kilos_facturables: Math.round(totalKilosFacturables * 100) / 100,
      }));
    }

    const errors = {};

    // Validar que el peso total de tallas no exceda el peso neto
    if (totalKilosFacturables > formData.peso_neto && formData.peso_neto > 0) {
      errors.tallas_peso = `El peso total de las tallas (${totalKilosFacturables.toFixed(2)} kg) excede el peso neto (${formData.peso_neto} kg)`;
    }

    // Validar que los bultos de las tallas coincidan con los bultos del apartado 3
    if (totalBultosTallas > 0 && formData.bultos > 0 && totalBultosTallas !== formData.bultos) {
      errors.tallas_bultos = `Los bultos de las tallas (${totalBultosTallas}) no coinciden con los bultos del apartado 3 (${formData.bultos})`;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors((prev) => ({
        ...prev,
        ...errors,
      }));
      return false;
    }

    setValidationErrors((prev) => {
      const { tallas_peso, tallas_bultos, ...rest } = prev;
      return rest;
    });
    return true;
  }, [formData.tallas, formData.peso_neto, formData.bultos, formData.kilos_facturables]);

  useEffect(() => {
    validateTallas();
  }, [formData.tallas, formData.peso_neto, formData.bultos]);

  // Manejar cambio de número de control
  const handleNumeroControlChange = async (value) => {
    // El valor ya viene formateado con ceros (ej: "000029")
    if (!value || value === "000000") {
      setFormData((prev) => ({ ...prev, numero_control: null, factura_origen: "" }));
      return;
    }

    const numeroInt = parseInt(value, 10);
    const numeroFormateado = String(value).padStart(6, "0");

    // Verificar duplicidad
    try {
      const response = await api.get(`/batches/check-control-number/${numeroInt}`);
      if (response.data.exists && (!isEditing || formData.numero_control !== numeroFormateado)) {
        setValidationErrors((prev) => ({
          ...prev,
          numero_control: "Este número de control ya existe",
        }));
      } else {
        setValidationErrors((prev) => {
          const { numero_control, ...rest } = prev;
          return rest;
        });

        // Generar factura origen con el número completo (AAAAMMDD + 000029)
        // Usar fecha personalizada si existe, si no usar fecha actual
        let facturaDate;
        if (formData.fecha_factura_origen) {
          facturaDate = new Date(formData.fecha_factura_origen);
        } else {
          facturaDate = new Date();
        }
        const year = facturaDate.getFullYear();
        const month = (facturaDate.getMonth() + 1).toString().padStart(2, "0");
        const day = facturaDate.getDate().toString().padStart(2, "0");
        const facturaOrigen = `${year}${month}${day}${numeroFormateado}`;

        setFormData((prev) => ({
          ...prev,
          numero_control: numeroFormateado,
          factura_origen: facturaOrigen,
        }));
      }
    } catch (error) {
      console.error("Error checking control number:", error);
    }
  };

  // Recalcular factura origen cuando cambia la fecha personalizada
  const handleFechaFacturaOrigenChange = (fechaStr) => {
    let facturaDate;
    if (fechaStr) {
      facturaDate = new Date(fechaStr);
    } else {
      facturaDate = new Date();
    }
    
    const year = facturaDate.getFullYear();
    const month = (facturaDate.getMonth() + 1).toString().padStart(2, "0");
    const day = facturaDate.getDate().toString().padStart(2, "0");
    
    // Solo recalcular si hay número de control
    const numeroControl = formData.numero_control || "000000";
    const facturaOrigen = `${year}${month}${day}${numeroControl}`;
    
    // Si la fecha es diferente al día actual, mostrar diálogo para cotización EUR/USD
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    
    if (fechaStr && fechaStr !== todayStr) {
      setShowCotizacionDialog(true);
    }
    
    setFormData((prev) => ({
      ...prev,
      fecha_factura_origen: fechaStr,
      factura_origen: facturaOrigen,
    }));
  };
  
  // Confirmar cotización EUR/USD cuando la fecha de factura es diferente al día actual
  const handleConfirmCotizacion = () => {
    const cotizacion = parseFloat(tempCotizacion) || 0;
    setFormData((prev) => ({
      ...prev,
      cotizacion_eur_usd: cotizacion,
    }));
    setShowCotizacionDialog(false);
    setTempCotizacion("");
  };

  // Manejar cambio de cliente
  const handleClientChange = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    setFormData((prev) => ({
      ...prev,
      cliente_id: clientId,
      cliente_nombre: client?.nombre || "",
    }));
  };

  // Manejar cambio de proveedor
  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find((s) => s.id === supplierId);
    setFormData((prev) => ({
      ...prev,
      proveedor_id: supplierId,
      proveedor_nombre: supplier?.nombre || "",
    }));
  };

  // Manejar cambio de logística
  const handleLogisticsChange = (logisticsId) => {
    const logistic = logistics.find((l) => l.id === logisticsId);
    setFormData((prev) => ({
      ...prev,
      logistica_id: logisticsId,
      logistica_nombre: logistic?.nombre || "",
    }));
  };

  // Manejar cambio de compañía de transporte
  const handleTransportChange = (transportId) => {
    const company = transportCompanies.find((t) => t.id === transportId);
    setFormData((prev) => ({
      ...prev,
      transporte_id: transportId,
      transporte_nombre: company?.nombre || "",
    }));
  };

  // Manejar cambio de talla
  const handleTallaChange = (index, field, value) => {
    const newTallas = [...formData.tallas];
    newTallas[index] = { ...newTallas[index], [field]: value };
    setFormData((prev) => ({ ...prev, tallas: newTallas }));
  };

  // Actualizar múltiples campos de una talla a la vez
  const handleTallaMultiChange = (index, updates) => {
    const newTallas = [...formData.tallas];
    newTallas[index] = { ...newTallas[index], ...updates };
    setFormData((prev) => ({ ...prev, tallas: newTallas }));
  };

  // Manejar cambio de peso cajas
  const handlePesoCajasChange = (value) => {
    const numValue = parseFloat(value);
    if (numValue === 0) {
      setShowWarning("Si seleccionas 0 Kg es porque va en formato cava de corcho en aéreo, cuidado con los pesos.");
    }
    setFormData((prev) => ({ ...prev, peso_cajas: numValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.producto_id) {
      toast.error("El producto es obligatorio");
      return;
    }
    if (!formData.tipo_envio) {
      toast.error("El tipo de envío es obligatorio");
      return;
    }
    if (!formData.numero_lote) {
      toast.error("El número de lote es obligatorio");
      return;
    }
    
    // Validar que proveedor con múltiples empresas seleccione una
    if (proveedorTieneMultiples && !formData.proveedor_id) {
      toast.error("Debes seleccionar a qué empresa pertenece este lote");
      return;
    }

    // Validar que al menos una talla esté rellena
    const tallasRellenas = formData.tallas.filter((t) => t.talla);
    if (tallasRellenas.length === 0) {
      toast.error("Debe rellenar al menos una talla");
      return;
    }

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Hay errores de validación. Revise los campos marcados.");
      return;
    }

    setSaving(true);
    try {
      // Sanitizar payload: convertir strings vacíos a null para campos numéricos
      const sanitizedData = { ...formData };
      
      // Campos numéricos que deben ser null si están vacíos
      const numericFields = [
        'descuento_porcentaje', 'descuento_euros', 'destare_cliente',
        'precio_coste', 'precio_venta', 'cambio_bcv', 'cotizacion_eur_usd'
      ];
      
      numericFields.forEach(field => {
        if (sanitizedData[field] === "" || sanitizedData[field] === undefined) {
          sanitizedData[field] = null;
        }
      });
      
      if (isEditing) {
        await api.put(`/batches/${id}`, sanitizedData);
        toast.success("Lote actualizado correctamente");
      } else {
        await api.post("/batches", sanitizedData);
        toast.success("Lote creado correctamente");
      }
      navigate("/batches");
    } catch (error) {
      // Manejar errores de validación de FastAPI (que vienen como array)
      const detail = error.response?.data?.detail;
      let errorMsg = "Error al guardar el lote";
      
      if (detail) {
        if (Array.isArray(detail)) {
          // FastAPI validation errors: [{loc: [...], msg: "...", type: "..."}]
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(", ");
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const DatePickerField = ({ label, value, onChange, disabled, error }) => {
    // Convertir string YYYY-MM-DD a Date sin problemas de zona horaria
    const parseLocalDate = (dateStr) => {
      if (!dateStr) return undefined;
      const [year, month, day] = dateStr.split("-").map(Number);
      return new Date(year, month - 1, day);
    };
    
    // Formatear Date a YYYY-MM-DD manteniendo la fecha local
    const formatLocalDate = (date) => {
      if (!date) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    
    return (
      <div className="space-y-2">
        <Label className={error ? "text-red-600" : ""}>{label}</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                "w-full justify-start text-left font-normal",
                !value && "text-muted-foreground",
                error && "border-red-500 text-red-600"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {value ? format(parseLocalDate(value), "dd/MM/yyyy", { locale: es }) : "Seleccionar fecha"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={parseLocalDate(value)}
              onSelect={(date) => onChange(formatLocalDate(date))}
              locale={es}
            />
          </PopoverContent>
        </Popover>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const filteredTransportCompanies = transportCompanies.filter(
    (c) => !formData.tipo_envio || c.tipo === formData.tipo_envio
  );

  return (
    <div className="space-y-6" data-testid="batch-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate("/batches")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEditing ? (isReadOnly ? "Ver Lote" : "Editar Lote") : "Nuevo Lote"}
          </h1>
          <p className="text-slate-500 mt-1">
            {isReadOnly ? "Vista de solo lectura" : isEditing ? "Modifica los datos del lote" : "Completa la información"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 1. Información Básica */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">1. Información Básica</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Producto - OBLIGATORIO PRIMERO */}
            <div className="space-y-2">
              <Label className="text-red-600">Producto *</Label>
              <Select value={formData.producto_id} onValueChange={handleProductChange} disabled={isReadOnly}>
                <SelectTrigger data-testid="select-producto">
                  <SelectValue placeholder="Seleccionar producto" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tipo de envío - OBLIGATORIO SEGUNDO */}
            <div className="space-y-2">
              <Label className="text-red-600">Tipo de Envío *</Label>
              <Select value={formData.tipo_envio} onValueChange={handleTipoEnvioChange} disabled={isReadOnly || !formData.producto_id}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="maritimo">
                    <div className="flex items-center gap-2">
                      <Ship className="w-4 h-4" /> Marítimo
                    </div>
                  </SelectItem>
                  <SelectItem value="aereo">
                    <div className="flex items-center gap-2">
                      <Plane className="w-4 h-4" /> Aéreo
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Número de lote - Editable manualmente */}
            <div className="space-y-2">
              <Label>Número de Lote {!numeroLoteManual && "(Auto)"}</Label>
              <Input
                value={formData.numero_lote}
                onChange={(e) => handleNumeroLoteChange(e.target.value)}
                className={cn("font-mono", numeroLoteManual ? "bg-white border-blue-300" : "bg-slate-50")}
                placeholder="Se genera automáticamente o escribe uno"
                disabled={isReadOnly}
              />
              {!isEditing && (
                <p className="text-xs text-slate-500">
                  {numeroLoteManual 
                    ? "✏️ Número editado manualmente" 
                    : "Se genera automáticamente al seleccionar producto y tipo de envío"}
                </p>
              )}
            </div>

            {/* Estado */}
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={formData.estado} onValueChange={(v) => setFormData((prev) => ({ ...prev, estado: v }))} disabled={isReadOnly}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="documentacion_inicial">Documentación inicial</SelectItem>
                  <SelectItem value="en_planta">En la planta</SelectItem>
                  <SelectItem value="en_reconocimiento">En reconocimiento</SelectItem>
                  <SelectItem value="listo_para_cargar">Listo para ser cargado</SelectItem>
                  <SelectItem value="en_transito">En tránsito</SelectItem>
                  <SelectItem value="en_destino">En destino</SelectItem>
                  <SelectItem value="entregado_cliente">Entregado a cliente</SelectItem>
                  <SelectItem value="completado">Completado</SelectItem>
                  <SelectItem value="anulado">Anulado</SelectItem>
                  <SelectItem value="rechazado">Rechazado</SelectItem>
                  <SelectItem value="otros">Otros</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Cliente - SOLO ADMIN */}
            {hasRole("admin") && (
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={formData.cliente_id} onValueChange={handleClientChange} disabled={isReadOnly}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Proveedor - Oculto para proveedores con 1 sola empresa (se asigna automáticamente) */}
            {/* Pero visible si el proveedor tiene múltiples empresas */}
            {(!isProveedor || proveedorTieneMultiples) && (
              <div className="space-y-2">
                <Label>Proveedor {proveedorTieneMultiples && <span className="text-red-500">*</span>}</Label>
                <Select value={formData.proveedor_id} onValueChange={handleSupplierChange} disabled={isReadOnly}>
                  <SelectTrigger>
                    <SelectValue placeholder={proveedorTieneMultiples ? "Selecciona tu empresa" : "Seleccionar proveedor"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSuppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {proveedorTieneMultiples && !formData.proveedor_id && (
                  <p className="text-xs text-amber-600">Debes seleccionar a qué empresa pertenece este lote</p>
                )}
              </div>
            )}

            {/* Operador Logístico */}
            <div className="space-y-2">
              <Label>Operador Logístico</Label>
              <Select value={formData.logistica_id} onValueChange={handleLogisticsChange} disabled={isReadOnly}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar operador" />
                </SelectTrigger>
                <SelectContent>
                  {logistics.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Compañía de Transporte */}
            <div className="space-y-2">
              <Label>Compañía de Transporte</Label>
              <Select value={formData.transporte_id} onValueChange={handleTransportChange} disabled={isReadOnly || !formData.tipo_envio}>
                <SelectTrigger>
                  <SelectValue placeholder={formData.tipo_envio ? "Seleccionar compañía" : "Seleccione tipo de envío primero"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredTransportCompanies.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Precios - según permisos */}
            {!isReadOnly && hasRole(["admin", "proveedor"]) && (
              <div className="space-y-2">
                <Label>Precio de Coste (USD)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.precio_coste}
                    onChange={(e) => setFormData((prev) => ({ ...prev, precio_coste: parseFloat(e.target.value) || 0 }))}
                    className="font-mono pr-12"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">USD</span>
                </div>
              </div>
            )}

            {hasRole("admin") && (
              <div className="space-y-2">
                <Label>Precio de Venta (EUR)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.precio_venta}
                    onChange={(e) => setFormData((prev) => ({ ...prev, precio_venta: parseFloat(e.target.value) || 0 }))}
                    className="font-mono pr-12"
                    disabled={isReadOnly}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">EUR</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Cambio BCV (USD → BS)</Label>
              <Input
                type="number"
                step="0.0001"
                value={formData.cambio_bcv}
                onChange={(e) => setFormData((prev) => ({ ...prev, cambio_bcv: parseFloat(e.target.value) || 0 }))}
                className="font-mono"
                disabled={isReadOnly}
                placeholder="Ej: 36.50"
              />
              <p className="text-xs text-slate-500">Tasa para convertir USD a Bolívares</p>
            </div>

            {/* Cotización EUR/USD - Oculto para proveedores */}
            {!isProveedor && (
              <div className="space-y-2">
                <Label>Cotización EUR/USD (opcional)</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={formData.cotizacion_eur_usd}
                  onChange={(e) => setFormData((prev) => ({ ...prev, cotizacion_eur_usd: parseFloat(e.target.value) || 0 }))}
                  className="font-mono"
                  disabled={isReadOnly}
                  placeholder="Auto si es fecha de hoy"
                />
                <p className="text-xs text-slate-500">
                  Si está vacío y la fecha es hoy, se obtiene automáticamente
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Certificado de Exportación</Label>
              <Input
                value={formData.certificado_exportacion}
                onChange={(e) => setFormData((prev) => ({ ...prev, certificado_exportacion: e.target.value }))}
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label>DUA</Label>
              <Input
                value={formData.dua}
                onChange={(e) => setFormData((prev) => ({ ...prev, dua: e.target.value }))}
                disabled={isReadOnly}
              />
            </div>

            {/* Campos de Descuento - Solo Admin */}
            {hasRole("admin") && (
              <>
                <div className="space-y-2">
                  <Label>Descuento % (sobre precio unitario)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={formData.descuento_porcentaje || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => ({ 
                        ...prev, 
                        descuento_porcentaje: val === "" ? "" : parseFloat(val),
                        descuento_euros: "" // Limpiar el otro campo
                      }));
                    }}
                    className="font-mono"
                    disabled={isReadOnly || (formData.descuento_euros !== "" && formData.descuento_euros !== null && formData.descuento_euros !== 0)}
                    placeholder="Ej: 5.5000"
                    data-testid="input-descuento-porcentaje"
                  />
                  {formData.descuento_porcentaje && formData.precio_venta > 0 && (
                    <p className="text-xs text-emerald-700 font-medium">
                      Precio final: {((formData.precio_venta || 0) * (1 - (parseFloat(formData.descuento_porcentaje) || 0) / 100)).toFixed(2)} €/Kg
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Descuento € (sobre precio unitario)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={formData.descuento_euros || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => ({ 
                        ...prev, 
                        descuento_euros: val === "" ? "" : parseFloat(val),
                        descuento_porcentaje: "" // Limpiar el otro campo
                      }));
                    }}
                    className="font-mono"
                    disabled={isReadOnly || (formData.descuento_porcentaje !== "" && formData.descuento_porcentaje !== null && formData.descuento_porcentaje !== 0)}
                    placeholder="Ej: 0.5000"
                    data-testid="input-descuento-euros"
                  />
                  {formData.descuento_euros && formData.precio_venta > 0 && (
                    <p className="text-xs text-emerald-700 font-medium">
                      Precio final: {((formData.precio_venta || 0) - (parseFloat(formData.descuento_euros) || 0)).toFixed(2)} €/Kg
                    </p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* 2. Fechas */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">2. Fechas</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <DatePickerField
              label="Fecha tentativa de embarque"
              value={formData.fecha_tentativa_embarque}
              onChange={(v) => setFormData((prev) => ({ ...prev, fecha_tentativa_embarque: v }))}
              disabled={isReadOnly}
            />

            <DatePickerField
              label="Fecha de Reconocimiento"
              value={formData.fecha_reconocimiento}
              onChange={(v) => {
                setFormData((prev) => ({ ...prev, fecha_reconocimiento: v }));
                setTimeout(validateFechaReconocimiento, 100);
              }}
              disabled={isReadOnly}
              error={validationErrors.fecha_reconocimiento}
            />

            <DatePickerField
              label="Salida del Buque"
              value={formData.salida_buque}
              onChange={(v) => {
                setFormData((prev) => ({ ...prev, salida_buque: v }));
                setTimeout(validateFechaReconocimiento, 100);
              }}
              disabled={isReadOnly}
            />

            <div className="space-y-2">
              <DatePickerField
                label="Llegada Estimada"
                value={formData.llegada_estimada}
                onChange={handleLlegadaEstimadaChange}
                disabled={isReadOnly}
              />
              {formData.desviacion_dias !== null && formData.desviacion_dias !== 0 && (
                <p className={`text-xs font-semibold ${formData.desviacion_dias > 0 ? "text-red-600" : "text-blue-600"}`}>
                  {formData.desviacion_dias > 0 
                    ? `🔴 Retrasado ${formData.desviacion_dias} días` 
                    : `🔵 Adelantado ${Math.abs(formData.desviacion_dias)} días`}
                  {formData.desviacion_porcentaje && ` (${Math.abs(formData.desviacion_porcentaje)}%)`}
                </p>
              )}
              {formData.llegada_estimada_original && formData.llegada_estimada !== formData.llegada_estimada_original && (
                <p className="text-xs text-slate-500">
                  Original: {(() => {
                    const datePart = formData.llegada_estimada_original.split('T')[0];
                    const parts = datePart.split('-');
                    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : formData.llegada_estimada_original;
                  })()}
                </p>
              )}
            </div>

            <DatePickerField
              label="Fecha de Captura"
              value={formData.fecha_captura}
              onChange={handleFechaCapturaChange}
              disabled={isReadOnly}
            />

            <DatePickerField
              label="Fecha de Congelación"
              value={formData.fecha_congelacion}
              onChange={handleFechaCongelacionChange}
              disabled={isReadOnly}
            />

            <DatePickerField
              label="Fecha de Caducidad"
              value={formData.fecha_caducidad}
              onChange={(v) => setFormData((prev) => ({ ...prev, fecha_caducidad: v }))}
              disabled={isReadOnly}
            />
          </CardContent>
        </Card>

        {/* 3. Pesos y Cantidades */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">3. Pesos y Cantidades</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Peso Neto (Kg) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.peso_neto}
                onChange={(e) => setFormData((prev) => ({ ...prev, peso_neto: parseFloat(e.target.value) || 0 }))}
                className="font-mono"
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label>Bultos</Label>
              <Input
                type="number"
                value={formData.bultos}
                onChange={(e) => setFormData((prev) => ({ ...prev, bultos: parseInt(e.target.value) || 0 }))}
                className="font-mono"
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label>Peso Cajas (Kg)</Label>
              <Input
                type="number"
                step="0.0000000001"
                min="0"
                max="2"
                value={formData.peso_cajas}
                onChange={(e) => handlePesoCajasChange(e.target.value)}
                className="font-mono"
                disabled={isReadOnly}
              />
              <p className="text-xs text-slate-500">0-2 Kg (hasta 10 decimales)</p>
            </div>

            <div className="space-y-2">
              <Label>Palets</Label>
              <Input
                type="number"
                value={formData.palets}
                onChange={(e) => setFormData((prev) => ({ ...prev, palets: parseInt(e.target.value) || 0 }))}
                className="font-mono"
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label>Kg Palet</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.kg_palet}
                onChange={(e) => setFormData((prev) => ({ ...prev, kg_palet: parseFloat(e.target.value) || 0 }))}
                className="font-mono"
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label>Peso Contenedor (Kg)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.peso_contenedor}
                onChange={(e) => setFormData((prev) => ({ ...prev, peso_contenedor: parseFloat(e.target.value) || 0 }))}
                className="font-mono"
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label>TARA (Kg)</Label>
              <Input
                type="number"
                value={formData.tara}
                className="font-mono bg-slate-50"
                readOnly
              />
              <p className="text-xs text-slate-500">(Bultos×Peso Cajas) + (Palets×Kg Palet)</p>
            </div>

            <div className="space-y-2">
              <Label>Peso Bruto (Kg)</Label>
              <Input
                type="number"
                value={formData.peso_bruto}
                className="font-mono bg-slate-50"
                readOnly
              />
              <p className="text-xs text-slate-500">Peso Neto + TARA + Contenedor</p>
            </div>

            {/* Kilos Facturables - Oculto para proveedores */}
            {!isProveedor && (
              <div className="space-y-2">
                <Label>Kilos Facturables (Kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.kilos_facturables}
                  onChange={(e) => setFormData((prev) => ({ ...prev, kilos_facturables: parseFloat(e.target.value) || 0 }))}
                  className="font-mono bg-blue-50 border-blue-200"
                  disabled={isReadOnly}
                />
                <p className="text-xs text-slate-500">Auto-calculado o editar manualmente (para Factura Cliente)</p>
              </div>
            )}

            {/* Destare Cliente - Oculto para proveedores */}
            {!isProveedor && (
              <div className="space-y-2">
                <Label>Destare Cliente (Kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.destare_cliente === "" || formData.destare_cliente === 0 ? "" : formData.destare_cliente}
                  onChange={(e) => setFormData((prev) => ({ ...prev, destare_cliente: e.target.value === "" ? "" : parseFloat(e.target.value) }))}
                  className="font-mono bg-amber-50 border-amber-200"
                  disabled={isReadOnly}
                  placeholder="0"
                />
                <p className="text-xs text-slate-500">Se suma a Kilos Facturables para la Factura Cliente</p>
                {formData.destare_cliente !== "" && formData.destare_cliente !== 0 && (
                  <p className="text-xs text-amber-700 font-medium">
                    Total para factura: {((formData.kilos_facturables || 0) + (parseFloat(formData.destare_cliente) || 0)).toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})} Kg
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>


        {/* Tallas y Etiquetas */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Tallas y Etiquetas</CardTitle>
            {(validationErrors.tallas_peso || validationErrors.tallas_bultos) && (
              <div className="mt-2 space-y-1">
                {validationErrors.tallas_peso && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    {validationErrors.tallas_peso}
                  </p>
                )}
                {validationErrors.tallas_bultos && (
                  <p className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    {validationErrors.tallas_bultos}
                  </p>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {formData.tallas.slice(0, 20).map((talla, idx) => {
                // Obtener empaques disponibles del producto seleccionado
                const selectedProduct = products.find(p => p.id === formData.producto_id);
                const empaquesDisponibles = selectedProduct ? [
                  selectedProduct.empaque,
                  selectedProduct.empaque2,
                  selectedProduct.empaque3,
                  selectedProduct.empaque4,
                ].filter(e => e && e.trim() !== '') : [];
                
                // Si solo hay un empaque, usarlo automáticamente
                const empaqueAutoSeleccionado = empaquesDisponibles.length === 1 ? empaquesDisponibles[0] : null;
                
                return (
                <div key={idx} className={`p-3 rounded-sm border ${(validationErrors.tallas_peso || validationErrors.tallas_bultos) ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-slate-500">Talla {idx + 1}</span>
                    {idx === 0 && <span className="text-xs text-red-500">*</span>}
                  </div>
                  <div className="space-y-2">
                    <Input
                      placeholder="Talla (ej: 2-3)"
                      value={talla.talla || ""}
                      onChange={(e) => handleTallaChange(idx, "talla", e.target.value)}
                      disabled={isReadOnly}
                      className="text-sm"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="number"
                        placeholder="Nº Bultos"
                        value={talla.num_bultos || ""}
                        onChange={(e) => handleTallaChange(idx, "num_bultos", parseInt(e.target.value) || null)}
                        disabled={isReadOnly || !talla.talla}
                        className="text-sm font-mono"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Kg/bulto"
                        value={talla.peso_por_bulto || ""}
                        onChange={(e) => handleTallaChange(idx, "peso_por_bulto", parseFloat(e.target.value) || null)}
                        disabled={isReadOnly || !talla.talla}
                        className="text-sm font-mono bg-green-50"
                        title="Auto-calculado o editar manualmente"
                      />
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Kilos Empaque (total)"
                      value={talla.kilos_empaque || ""}
                      onChange={(e) => {
                        const kilosEmpaque = parseFloat(e.target.value) || null;
                        const numBultos = talla.num_bultos || 1;
                        // Redondear hacia abajo a 2 decimales
                        const kgPorBulto = kilosEmpaque ? Math.floor((kilosEmpaque / numBultos) * 100) / 100 : null;
                        // Actualizar ambos campos a la vez
                        handleTallaMultiChange(idx, {
                          kilos_empaque: kilosEmpaque,
                          peso_por_bulto: kgPorBulto
                        });
                      }}
                      disabled={isReadOnly || !talla.talla}
                      className="text-sm font-mono mt-1"
                    />
                    {/* Selector de Empaque - solo si hay más de un empaque disponible */}
                    {empaquesDisponibles.length > 1 && talla.talla && (
                      <Select
                        value={talla.empaque_seleccionado || empaqueAutoSeleccionado || ""}
                        onValueChange={(value) => handleTallaChange(idx, "empaque_seleccionado", value)}
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Empaque" />
                        </SelectTrigger>
                        <SelectContent>
                          {empaquesDisponibles.map((emp, i) => (
                            <SelectItem key={i} value={emp}>{emp}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {/* Mostrar empaque único automáticamente */}
                    {empaquesDisponibles.length === 1 && talla.talla && (
                      <p className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded">
                        Empaque: {empaquesDisponibles[0]}
                      </p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">Kg Empaque ÷ Bultos = Kg/bulto</p>
                  </div>
                </div>
              )})}
            </div>
          </CardContent>
        </Card>

        {/* 4. Información del Envío */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">4. Información del Envío</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className={validationErrors.numero_control ? "text-red-600" : ""}>
                Número de Control
              </Label>
              <Input
                type="text"
                value={formData.numero_control || ""}
                onChange={(e) => {
                  // Permitir solo números y formatear con 6 dígitos
                  const value = e.target.value.replace(/\D/g, "");
                  const formatted = value.padStart(6, "0").slice(-6);
                  handleNumeroControlChange(formatted);
                }}
                placeholder="000000"
                maxLength={6}
                className={`font-mono ${validationErrors.numero_control ? "border-red-500" : ""}`}
                disabled={isReadOnly}
              />
              {validationErrors.numero_control && (
                <p className="text-xs text-red-600">{validationErrors.numero_control}</p>
              )}
            </div>

            {/* Fecha Factura Origen - Opcional (usando DatePicker consistente) */}
            <DatePickerField
              label="Fecha Factura Origen"
              value={formData.fecha_factura_origen}
              onChange={handleFechaFacturaOrigenChange}
              disabled={isReadOnly}
            />

            <div className="space-y-2">
              <Label>Factura Origen</Label>
              <Input
                value={formData.factura_origen}
                onChange={(e) => setFormData((prev) => ({ ...prev, factura_origen: e.target.value }))}
                className="font-mono"
                disabled={isReadOnly}
                placeholder="AAAAMMDD + Nº Control"
              />
              <p className="text-xs text-slate-500">Auto-generado o editar manualmente</p>
            </div>

            {/* Factura Destino - SOLO ADMIN */}
            {hasRole("admin") && (
              <div className="space-y-2">
                <Label>Factura Destino</Label>
                <Input
                  value={formData.factura_destino}
                  onChange={(e) => setFormData((prev) => ({ ...prev, factura_destino: e.target.value }))}
                  className="font-mono"
                  disabled={isReadOnly}
                />
              </div>
            )}

            {/* Fecha Factura Cliente - SOLO ADMIN (usando DatePicker consistente) */}
            {hasRole("admin") && (
              <DatePickerField
                label="Fecha Factura Cliente"
                value={formData.fecha_factura_cliente}
                onChange={(v) => setFormData((prev) => ({ ...prev, fecha_factura_cliente: v }))}
                disabled={isReadOnly}
              />
            )}

            <div className="space-y-2">
              <Label>Contenedor</Label>
              <Input
                value={formData.contenedor}
                onChange={(e) => setFormData((prev) => ({ ...prev, contenedor: e.target.value }))}
                placeholder="Ej: CGMU6972867"
                className="font-mono"
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label>Precintos</Label>
              <Input
                value={formData.precintos}
                onChange={(e) => setFormData((prev) => ({ ...prev, precintos: e.target.value }))}
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label>Identificación Buque</Label>
              <Input
                value={formData.identificacion_buque}
                onChange={(e) => setFormData((prev) => ({ ...prev, identificacion_buque: e.target.value }))}
                placeholder="Ej: CMA CGM WHITE (OERDBN1MA)"
                disabled={isReadOnly}
              />
            </div>

            {formData.tipo_envio === "maritimo" ? (
              <div className="space-y-2">
                <Label>BL o AWB</Label>
                <Input
                  value={formData.bl_maritimo}
                  onChange={(e) => setFormData((prev) => ({ ...prev, bl_maritimo: e.target.value }))}
                  className="font-mono"
                  disabled={isReadOnly}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>AWB (Guía aérea)</Label>
                <Input
                  value={formData.awb_aereo}
                  onChange={(e) => setFormData((prev) => ({ ...prev, awb_aereo: e.target.value }))}
                  className="font-mono"
                  disabled={isReadOnly}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Destino</Label>
              <Input
                value={formData.puerto_desembarco}
                onChange={(e) => setFormData((prev) => ({ ...prev, puerto_desembarco: e.target.value }))}
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label>Lugar de Destino (Almacén)</Label>
              <Select
                value={formData.lugar_destino_id || ""}
                onValueChange={(value) => {
                  const lugar = lugaresDestino.find((l) => l.id === value);
                  setFormData((prev) => ({
                    ...prev,
                    lugar_destino_id: value,
                    lugar_destino_nombre: lugar?.nombre || "",
                  }));
                }}
                disabled={isReadOnly}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar lugar de destino" />
                </SelectTrigger>
                <SelectContent>
                  {lugaresDestino.map((lugar) => (
                    <SelectItem key={lugar.id} value={lugar.id}>
                      {lugar.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Puesto Control Fronterizo</Label>
              <Select
                value={formData.puesto_control_id || ""}
                onValueChange={(value) => {
                  const puesto = puestosControl.find((p) => p.id === value);
                  setFormData((prev) => ({
                    ...prev,
                    puesto_control_id: value,
                    puesto_control_nombre: puesto?.nombre || "",
                  }));
                }}
                disabled={isReadOnly}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar puesto de control" />
                </SelectTrigger>
                <SelectContent>
                  {puestosControl.map((puesto) => (
                    <SelectItem key={puesto.id} value={puesto.id}>
                      {puesto.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Observaciones */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Observaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.observaciones}
              onChange={(e) => setFormData((prev) => ({ ...prev, observaciones: e.target.value }))}
              placeholder="Notas adicionales sobre el lote..."
              rows={4}
              disabled={isReadOnly}
            />
          </CardContent>
        </Card>

        {/* Comentarios Privados - Solo visible para admin y proveedor asignado */}
        {(user?.role === "admin" || (isProveedor && formData.proveedor_id === user?.proveedor_id)) && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <span>Comentarios PRIVADOS</span>
                <span className="text-xs font-normal text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
                  Solo admin y proveedor asignado
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.comentarios_privados}
                onChange={(e) => setFormData((prev) => ({ ...prev, comentarios_privados: e.target.value }))}
                placeholder="Notas privadas que solo verá el administrador y el proveedor asignado a este lote..."
                rows={4}
                disabled={isReadOnly}
                className="bg-white"
              />
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {!isReadOnly && (
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate("/batches")}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? "Actualizar" : "Crear"} Lote
                </>
              )}
            </Button>
          </div>
        )}
      </form>

      {/* Warning Dialog */}
      <AlertDialog open={!!showWarning} onOpenChange={() => setShowWarning(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Aviso
            </AlertDialogTitle>
            <AlertDialogDescription>{showWarning}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowWarning(null)}>
              Entendido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cotización EUR/USD Dialog */}
      <AlertDialog open={showCotizacionDialog} onOpenChange={setShowCotizacionDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              💱 Cotización EUR/USD
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  La fecha de factura seleccionada ({formData.fecha_factura_origen}) es diferente a hoy.
                  Por favor, introduce la cotización EUR/USD del día de la factura.
                </p>
                <div className="space-y-2">
                  <Label>Cotización EUR/USD (ej: 1.0850)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    placeholder="1.0850"
                    value={tempCotizacion}
                    onChange={(e) => setTempCotizacion(e.target.value)}
                    className="font-mono"
                  />
                  <p className="text-xs text-slate-500">
                    Este valor se usará para calcular TOTAL EUR CFR = Total USD / Cotización
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowCotizacionDialog(false);
              setTempCotizacion("");
            }}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmCotizacion}
              disabled={!tempCotizacion || parseFloat(tempCotizacion) <= 0}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
